import { CalendarCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { parseCalendarDate } from '@/lib/calendar-date'
import type { DayListGroup } from './day-list'
import { DayListGroups } from './day-list-groups'

interface DaySheetProps {
  /** Property-local `YYYY-MM-DD`: what `?open=day:` names. */
  day: string
  /**
   * `pending` while the month on screen is still the last one's data (paging);
   * `outside` for a day the month shown does not hold (a pasted link).
   */
  state: 'pending' | 'outside' | 'ok'
  groups: DayListGroup[]
  /** Layers that are switched off or failed, in words: "Deposits (switched off)". */
  notShown: string[]
  /** What to say for a day `outside` the data shown; the property tab's words when left out. */
  outsideMessage?: string
  /** Opens the "Block dates" form for this day. Left out, the day list has no such button. */
  onBlockDates?: () => void
  onClose: () => void
}

function dayHeading(day: string): string {
  const date = parseCalendarDate(day)
  return date
    ? date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : day
}

/**
 * Everything on one day of the property calendar, opened from the day number or
 * from a cell's "+N more".
 *
 * Read-only, like the grid: each row opens the sheet of the thing it names (a
 * stay, a cleaning, a deposit timeline, a supply category, a gap) by the same
 * `open=` value the grid's own bars and blocks use. Only what the grid is
 * drawing is listed, so a layer that is switched off or failed is named at the
 * foot rather than silently missing.
 */
export function DaySheet({
  day,
  state,
  groups,
  notShown,
  outsideMessage = 'This day is not in the month shown. Go to its month to see what is on it.',
  onBlockDates,
  onClose,
}: DaySheetProps) {
  const total = groups.reduce((sum, group) => sum + group.rows.length, 0)
  return (
    <Sheet open onOpenChange={open => !open && onClose()}>
      <SheetContent aria-describedby="day-sheet-description" data-testid="day-sheet">
        <SheetHeader>
          <SheetTitle>{dayHeading(day)}</SheetTitle>
          <SheetDescription id="day-sheet-description">
            {state === 'ok'
              ? total === 0
                ? // Only a day read in full may be called empty.
                  notShown.length > 0
                  ? 'Nothing to open on this day in the layers shown.'
                  : 'Nothing to open on this day.'
                : `${total} ${total === 1 ? 'thing' : 'things'} to open on this day.`
              : 'Calendar'}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-5">
          {state === 'pending' ? (
            <div className="space-y-3" aria-busy="true">
              <LoadingSkeleton className="h-6 w-1/2" />
              <LoadingSkeleton className="h-20 w-full" />
            </div>
          ) : state === 'outside' ? (
            <p className="text-sm text-textMuted" data-testid="day-sheet-outside">
              {outsideMessage}
            </p>
          ) : (
            <>
              <DayListGroups groups={groups} />
              {onBlockDates && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onBlockDates}
                  data-testid="day-sheet-block-dates"
                >
                  <CalendarCheck className="mr-1.5 h-4 w-4" aria-hidden />
                  Block dates
                </Button>
              )}
              {notShown.length > 0 && (
                <p className="text-xs text-textMuted" data-testid="day-sheet-not-shown">
                  Not shown: {notShown.join(', ')}.
                </p>
              )}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
